import { FilterList } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { CustomerSelect, DatePicker, MaterialSelect } from '../../../Components/CommonInputs';
import { CustomerDto, MaterialDto } from '../../../dtos';
import { emptyGuid } from '../../../util';

export interface IOrderFilterOptions {
    customerId: string;
    customerLabel: string;
    materialId: string;
    materialLabel: string;
    orderDate: Date | undefined;
}

export const defaultOrderFilterOptions: IOrderFilterOptions = {
    customerId: emptyGuid,
    customerLabel: '',
    materialId: emptyGuid,
    materialLabel: '',
    orderDate: undefined,
};

export interface IOrderFilterDialogProps {
    isVisible: boolean;
    onClose: () => void;
    filterProps: IOrderFilterOptions;
    setFilterProps: (updatedFilters: IOrderFilterOptions) => void;
    /** When false, hide customer filter (e.g. job-scoped order list). */
    showCustomerFilter?: boolean;
}

export const OrderFilterDialog: FC<IOrderFilterDialogProps> = (props) => {
    const { isVisible, onClose, filterProps, setFilterProps, showCustomerFilter = true } = props;
    const [localFilters, setLocalFilters] = useState<IOrderFilterOptions>(filterProps);

    useEffect(() => {
        if (isVisible) {
            setLocalFilters(filterProps);
        }
    }, [filterProps, isVisible]);

    const isFormClean =
        filterProps.customerId === localFilters.customerId &&
        filterProps.materialId === localFilters.materialId &&
        filterProps.orderDate?.getTime() === localFilters.orderDate?.getTime();

    const handleClose = () => {
        onClose();
    };

    const handleFilter = () => {
        setFilterProps({ ...localFilters });
        handleClose();
    };

    const handleClear = () => {
        setLocalFilters({ ...defaultOrderFilterOptions });
    };

    const handleCustomerChange = (selected?: CustomerDto) => {
        if (!selected) {
            setLocalFilters({
                ...localFilters,
                customerId: emptyGuid,
                customerLabel: '',
            });
        } else {
            setLocalFilters({
                ...localFilters,
                customerId: selected.id,
                customerLabel: selected.name,
            });
        }
    };

    const handleMaterialChange = (selected?: MaterialDto) => {
        if (!selected) {
            setLocalFilters({
                ...localFilters,
                materialId: emptyGuid,
                materialLabel: '',
            });
        } else {
            setLocalFilters({
                ...localFilters,
                materialId: selected.id,
                materialLabel: selected.code ?? selected.description ?? '',
            });
        }
    };

    const handleOrderDateChange = (date?: Date | null) => {
        setLocalFilters({
            ...localFilters,
            orderDate: date ?? undefined,
        });
    };

    return (
        <Dialog open={isVisible} onClose={handleClose} fullWidth maxWidth='md' PaperProps={{ sx: { width: '100%' } }}>
            <DialogTitle>
                <Typography variant='h2'>
                    <FilterList /> Filter Orders
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ marginTop: 3 }}>
                <Grid container direction='row' spacing={2}>
                    {showCustomerFilter && (
                        <Grid item xs={12} sm={6}>
                            <CustomerSelect
                                selectedCustomerId={localFilters.customerId}
                                handleSelectedCustomerChange={handleCustomerChange}
                                label='Customer'
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={showCustomerFilter ? 6 : 12}>
                        <MaterialSelect selectedMaterialId={localFilters.materialId} handleSelectedMaterialChange={handleMaterialChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DatePicker label='Order date' value={localFilters.orderDate} onChange={handleOrderDateChange} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
                <Grid container direction='row' justifyContent={'space-between'}>
                    <Grid item>
                        <Button onClick={handleClear} size='large'>
                            Clear All
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant='outlined' style={{ boxShadow: 'none', marginRight: '15px' }} onClick={handleClose}>
                            {isFormClean ? 'Close' : 'Cancel'}
                        </Button>
                        <Button variant='contained' size='medium' onClick={handleFilter} sx={{ color: 'error', backgroundColor: 'primary' }}>
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};
